import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button, TextInput, TextArea, Dropdown, DatePicker } from '../../../shared/ui'
import { StarRating } from './StarRating'
import { useSubmitProbearbeiter, type Empfehlung } from './onboardingApi'
import { isValidEmail, isValidPhone } from './lookups'

// School lookup is intentionally a free-text field for v1 — the Probearbeiter
// form gets used quickly on-site and PL/SL know their school's FM-ID by heart.
// A typeahead via /api/personen-grouped or a school registry can be added later.

export function ProbearbeiterFormPage() {
  const navigate = useNavigate()
  const submit = useSubmitProbearbeiter()
  const schoolsQ: { data?: { rows: Array<{ id: string; name: string }> } } = { data: { rows: [] } }

  const [vorname, setVorname]         = useState('')
  const [nachname, setNachname]       = useState('')
  const [email, setEmail]             = useState('')
  const [handy, setHandy]             = useState('')
  const [schuleId, setSchuleId]       = useState('')
  const [von, setVon]                 = useState('')
  const [bis, setBis]                 = useState('')
  const [stunden, setStunden]         = useState('')
  const [rPunkt, setRPunkt]           = useState<number | null>(null)
  const [rHyg, setRHyg]               = useState<number | null>(null)
  const [rTeam, setRTeam]             = useState<number | null>(null)
  const [rEng, setREng]               = useState<number | null>(null)
  const [empfehlung, setEmpfehlung]   = useState<Empfehlung | ''>('')
  const [notizen, setNotizen]         = useState('')
  const [error, setError]             = useState<string | null>(null)

  function validate(): string | null {
    if (!vorname.trim() || !nachname.trim()) return 'Vor- und Nachname sind Pflichtfelder.'
    if (!isValidEmail(email)) return 'Gültige E-Mail-Adresse benötigt — wird für Rückmeldung gebraucht.'
    if (handy && !isValidPhone(handy)) return 'Mobiltelefon-Format ungültig.'
    if (!schuleId) return 'Schule auswählen.'
    if (!von || !bis) return 'Probearbeitsdaten (Von/Bis) ausfüllen.'
    if (von > bis) return 'Probe-Bis muss ≥ Probe-Von sein.'
    return null
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    const v = validate()
    if (v) { setError(v); return }
    try {
      const schule = schoolsQ.data?.rows.find(s => s.id === schuleId)
      const r = await submit.mutateAsync({
        vorname: vorname.trim(),
        nachname: nachname.trim(),
        email: email.trim(),
        handy: handy.trim() || null,
        probe_schule_id: schuleId,
        probe_schule_name: schule?.name ?? null,
        probe_von_datum: von,
        probe_bis_datum: bis,
        probe_stunden_gesamt: stunden ? Number(stunden.replace(',', '.')) : null,
        probe_rating_punktlichkeit: rPunkt,
        probe_rating_hygiene: rHyg,
        probe_rating_teamfaehigkeit: rTeam,
        probe_rating_engagement: rEng,
        probe_empfehlung: empfehlung === '' ? null : empfehlung,
        probe_notizen: notizen.trim() || null,
      })
      navigate(`/hr/onboarding?submitted=${r.main_id}`)
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Übermittlung fehlgeschlagen')
    }
  }

  const schoolOptions = (schoolsQ.data?.rows ?? []).map(s => ({ value: s.id, label: s.name }))

  return (
    <div className="max-w-2xl mx-auto p-4 sm:p-8">
      <h1 className="text-2xl font-semibold mb-2">Neue/r Probearbeiter:in</h1>
      <p className="text-sm text-on-surface-variant mb-6">
        Tragen Sie die Eingangsdaten und Ihre Bewertung der Probearbeit ein. HR sieht das Ergebnis und entscheidet
        über Annahme oder Ablehnung. Eine Rückmeldung per E-Mail an die angegebene Adresse erfolgt durch HR.
      </p>

      <form onSubmit={handleSubmit} className="space-y-6">
        <section className="space-y-3">
          <h2 className="text-[11px] font-semibold uppercase tracking-wider text-on-surface-variant border-b border-outline-variant/30 pb-1.5">
            Stammdaten
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <TextInput variant="glass" label="Vorname *" value={vorname} onChange={e => setVorname(e.target.value)} />
            <TextInput variant="glass" label="Nachname *" value={nachname} onChange={e => setNachname(e.target.value)} />
            <TextInput variant="glass" label="E-Mail *" type="email" value={email} onChange={e => setEmail(e.target.value)} />
            <TextInput variant="glass" label="Mobil" type="tel" value={handy} onChange={e => setHandy(e.target.value)} />
          </div>
        </section>

        <section className="space-y-3">
          <h2 className="text-[11px] font-semibold uppercase tracking-wider text-on-surface-variant border-b border-outline-variant/30 pb-1.5">
            Eingangsdaten
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {schoolOptions.length > 0 ? (
              <Dropdown variant="glass"
                label="Schule *"
                value={schuleId}
                options={schoolOptions}
                onChange={setSchuleId}
                searchable
                placeholder="Schule auswählen"
              />
            ) : (
              <TextInput variant="glass" label="Schule (FM-ID) *" value={schuleId} onChange={e => setSchuleId(e.target.value)} />
            )}
            <TextInput variant="glass" label="Stunden gesamt" type="number" inputMode="decimal" value={stunden} onChange={e => setStunden(e.target.value)} />
            <DatePicker label="Probe Von *" value={von} onChange={setVon} />
            <DatePicker label="Probe Bis *" value={bis} onChange={setBis} min={von || undefined} />
          </div>
        </section>

        <section className="space-y-4">
          <h2 className="text-[11px] font-semibold uppercase tracking-wider text-on-surface-variant border-b border-outline-variant/30 pb-1.5">
            Bewertung (5 Sterne — leer = nicht bewertet)
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <StarRating label="Pünktlichkeit"            value={rPunkt} onChange={setRPunkt} />
            <StarRating label="Hygiene"                  value={rHyg}   onChange={setRHyg} />
            <StarRating label="Teamfähigkeit"            value={rTeam}  onChange={setRTeam} />
            <StarRating label="Engagement / Eigeninitiative" value={rEng} onChange={setREng} />
          </div>
          <Dropdown variant="glass"
            label="Empfehlung"
            value={empfehlung}
            options={[
              { value: 'ja',         label: 'Ja — sofort einstellen' },
              { value: 'vielleicht', label: 'Vielleicht — HR entscheidet' },
              { value: 'nein',       label: 'Nein — passt nicht' },
            ]}
            onChange={v => setEmpfehlung(v as Empfehlung | '')}
            placeholder="—"
          />
          <TextArea variant="glass"
            label="Notizen für HR (frei)"
            value={notizen}
            onChange={e => setNotizen(e.target.value)}
            rows={4}
            placeholder="Auffälligkeiten, Stärken, Schwächen, Sprachkenntnisse, …"
          />
        </section>

        {error && (
          <div className="rounded-xl border border-error/40 bg-error-container/30 px-4 py-3 text-sm text-on-error-container">
            {error}
          </div>
        )}

        <div className="flex items-center justify-end gap-2 pt-4 border-t border-outline-variant/30">
          <Button variant="ghost" onClick={() => navigate('/hr/onboarding')}>Abbrechen</Button>
          <Button variant="primary" type="submit" disabled={submit.isPending}>
            {submit.isPending ? 'Sende…' : 'An HR senden'}
          </Button>
        </div>
      </form>
    </div>
  )
}
